/**
 * Review a share link a connected AI app (an MCP client) created as this person — opened from the bell's
 * `share_link_from_connection` row (any link: public or restricted).
 *
 * The row used to REVOKE on click, with nothing shown first: the person had to decide from one line of text, and a
 * click meant to read more killed a link they may have asked for. This dialog shows WHO made it (which connection,
 * acting as whom) and WHEN, above the link's own card — the same `LinkRow` the share dialog and Settings → Links show
 * (audience, token, URL, recipients, views, expiry), read-only — and leaves the decision (Keep / Revoke) to them.
 *
 * The connection is drawn with the `AppTile` Settings → Connected apps uses, so a connection looks the same wherever
 * it appears. No drawn borders beyond the card's own glass hairline (docs/DESIGN_DOC.md rule 7).
 *
 * Every request carries the link's OWN tenant + org email (the bell lists every tenant's rows), in lowercase keys —
 * they replace the api-client's active-tenant defaults (a differently-cased key was sent alongside and refused). The
 * card is read-only for the same reason: its edit panel would act in the ACTIVE org.
 */
import { useEffect, useMemo, useState } from 'react'
import { Ban } from 'lucide-react'
import { AppTile, FileIcon, Modal, fileIconProps, useToast } from '@papan/core-ui/ui'
import { request, errorText } from '@papan/api-client'
import { LinkRow, LinkQrCode } from '@papan/share-links/ui'
import type { ShareLinkWithUrl } from '@papan/share-links/client'
import { useTranslation } from '@papan/i18n'

interface Brief {
  id: string
  revoked: boolean
  expired: boolean
  createdAt?: string | null
  createdBy?: { name: string | null; email: string } | null
  rootFileId?: string | null
  revokeReason?: string | null
  /** Who revoked it and when (the audit trail) — null for an automatic revoke or one from before it was recorded. */
  revokedBy?: { name: string | null; email: string } | null
  revokedAt?: string | null
  /** What the link opens, as the caller may see it (#787) — null when they can't be shown where. */
  target?: { id: string; parentId: string | null; name: string; isFolder: boolean; fileType: string | null; path: string } | null
}

export interface ShareLinkReviewTarget {
  linkId: string
  tenantId: string | null
  orgEmail: string | null
  targetName: string | null
  connectionName: string | null
  /** When the notification was written — the fallback "created" time if the link can't be read. */
  notifiedAt: string | null
}

/** Where the link lives relative to the active org. The card's edit panel acts in the ACTIVE org (and identity), so
 *  for a link elsewhere its edit button first switches there (`goInOwner`) — this dialog stays open. */
export interface ShareLinkReviewPlace {
  editableHere: boolean
  /** The link's org by name; null for a personal account. */
  orgName: string | null
  /** Go to a page in the link's org (`rest`, e.g. `/folder/<id>?reveal=<id>`), switching to it when it isn't the
   *  active one; no `rest` = stay on the current page. */
  goInOwner: (rest?: string) => Promise<void>
}

export function ShareLinkReviewDialog({ target, place, onClose }: { target: ShareLinkReviewTarget; place: ShareLinkReviewPlace; onClose: () => void }) {
  const { t, i18n } = useTranslation('common')
  const toast = useToast()
  const headers = useMemo<Record<string, string> | undefined>(() => (target.tenantId
    ? { 'x-tenant-id': target.tenantId, ...(target.orgEmail ? { 'x-org-email': target.orgEmail } : {}) }
    : undefined), [target.tenantId, target.orgEmail])

  const [brief, setBrief] = useState<Brief | null>(null)
  const [link, setLink] = useState<ShareLinkWithUrl | null>(null)
  /** 'gone' = the link no longer exists or isn't this person's to see (404). */
  const [loadState, setLoadState] = useState<'loading' | 'ok' | 'gone' | 'error'>('loading')
  const [revoking, setRevoking] = useState(false)
  const [showQr, setShowQr] = useState(false)
  const [editing, setEditing] = useState(false)
  const [switching, setSwitching] = useState(false)
  // Opening the target or editing the link acts in the link's own org. When that isn't the active one, ask first —
  // the switch changes the whole app's org (and identity). Asked in a STACKED `Modal`: it portals into its own new
  // <body> child, so it takes clicks — `useConfirm()` renders inside the app root, which this modal makes inert (it
  // froze, unclickable).
  const [askSwitch, setAskSwitch] = useState<((ok: boolean) => void) | null>(null)
  const confirmSwitch = (): Promise<boolean> => {
    if (place.editableHere) return Promise.resolve(true)
    return new Promise((resolve) => setAskSwitch(() => (ok: boolean) => { setAskSwitch(null); resolve(ok) }))
  }
  const switchOrg = place.orgName ?? t('shareLinkReview.personalAccount')
  // Reveal the target in its folder in the drive (in the link's org — switched to if needed); the dialog closes.
  const reveal = async () => {
    const tgt = brief?.target
    if (!tgt) return
    const atTop = !parentPath
    const rest = atTop || !tgt.parentId ? `/folder?reveal=${encodeURIComponent(tgt.id)}` : `/folder/${encodeURIComponent(tgt.parentId)}?reveal=${encodeURIComponent(tgt.id)}`
    if (!(await confirmSwitch())) return
    setSwitching(true)
    try { await place.goInOwner(rest); onClose() } finally { setSwitching(false) }
  }
  const startEdit = async () => {
    if (place.editableHere) { setEditing(true); return }
    if (!(await confirmSwitch())) return
    setSwitching(true)
    try { await place.goInOwner(); setEditing(true) } finally { setSwitching(false) }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const b = await request<Brief>(`/api/share-links/${encodeURIComponent(target.linkId)}/brief`, headers ? { headers } : undefined)
      if (cancelled) return
      setBrief(b)
      // The card row, from the same list the share dialog reads (its authority filter included).
      const qs = new URLSearchParams({ includeRevoked: '1', ...(b.rootFileId ? { fileId: b.rootFileId } : {}) })
      const { shareLinks } = await request<{ shareLinks: ShareLinkWithUrl[] }>(`/api/share-links?${qs}`, headers ? { headers } : undefined)
      if (cancelled) return
      setLink(shareLinks.find((l) => l.id === target.linkId) ?? null)
      setLoadState('ok')
    })().catch((err) => {
      if (cancelled || errorText(err) === null) return
      const status = (err as { status?: number })?.status
      setLoadState(status === 404 || status === 410 ? 'gone' : 'error')
    })
    return () => { cancelled = true }
  }, [target.linkId, headers])

  const fmt = (iso: string | null | undefined) => {
    if (!iso) return null
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
  }

  const live = loadState === 'ok' && !!brief && !brief.revoked && !brief.expired
  const what = brief?.target?.name ?? target.targetName ?? t('shareLinkReview.anItem')
  // The folders ABOVE the target, so two items with the same name read apart. The walk starts at the drive's own root
  // row, which is not a folder anyone sees by name — drop it.
  const parentPath = brief?.target ? brief.target.path.split(' / ').slice(1, -1).join(' / ') : null
  const via = target.connectionName ?? t('shareLinkReview.aConnectedApp')
  const creatorEmail = target.orgEmail ?? brief?.createdBy?.email ?? null
  const who = brief?.createdBy?.name?.trim() || creatorEmail
  const when = fmt(brief?.createdAt) ?? fmt(target.notifiedAt)
  const revoked = loadState === 'gone' || !!brief?.revoked
  const revokerName = brief?.revokedBy ? (brief.revokedBy.name?.trim() || brief.revokedBy.email) : null
  const revokedNote = !revoked ? null
    : revokerName
      ? t(brief?.revokeReason === 'owner_revoked' ? 'shareLinkReview.revokedByOwner' : 'shareLinkReview.revokedBy', { who: revokerName, when: fmt(brief?.revokedAt) ?? '' })
      : brief?.revokeReason === 'file_deleted' ? t('shareLinkReview.revokedAuto') : t('shareLinkReview.revokedUnknown')

  const revoke = async () => {
    setRevoking(true)
    try {
      await request(`/api/share-links/${encodeURIComponent(target.linkId)}`, { method: 'DELETE', headers })
      toast.success(t('shareLinkReview.revoked'))
      onClose()
    } catch (err) {
      const statusCode = (err as { status?: number })?.status
      if (statusCode === 404 || statusCode === 410) { toast.info(t('shareLinkReview.alreadyRevoked')); onClose(); return }
      const text = errorText(err, t('shareLinkReview.revokeFailed'))
      if (text !== null) toast.error(text)
    } finally {
      setRevoking(false)
    }
  }

  const copy = async () => {
    if (!link?.url) return
    try { await navigator.clipboard.writeText(link.url); toast.success(t('shareLinkReview.copied')) } catch { /* refused — the URL is a link in the card */ }
  }

  return (
    <>
    <Modal
      glass
      title={t('shareLinkReview.title', { what })}
      onClose={onClose}
      // Not while the switch question is up: both dialogs hear Escape, and it must close only the top one.
      dismissible={!revoking && !askSwitch}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={revoking}
            className="rounded-lg px-3 py-1.5 text-sm themed-text themed-bg-hover disabled:opacity-50"
          >
            {live ? t('shareLinkReview.keep') : t('close')}
          </button>
          {live && (
            <button
              type="button"
              onClick={revoke}
              disabled={revoking}
              className="rounded-lg px-4 py-1.5 text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
            >
              {revoking ? t('shareLinkReview.revoking') : t('shareLinkReview.revoke')}
            </button>
          )}
        </>
      }
    >
      <div className="px-5 py-4 space-y-4">
        {/* What it opens, then the link's own card (to whom, how, views), then who made it and when. */}
        <div className="space-y-2">
          {brief?.target && (
            <div className="flex items-center gap-3 px-1">
              <FileIcon {...fileIconProps(brief.target)} size={32} className="shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium themed-text truncate" title={brief.target.name}>{brief.target.name}</p>
                <p className="text-xs themed-text-muted truncate" title={brief.target.path}>
                  {/* At the top of its drive: the org's name, or "My Drive" for a personal account. */}
                  {parentPath || place.orgName || t('shareLinkReview.driveRoot')}
                </p>
              </div>
              <button
                type="button"
                onClick={reveal}
                disabled={switching}
                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-medium themed-text themed-bg-hover disabled:opacity-50"
                title={t('shareLinkReview.openInDriveHint')}
              >
                {t('shareLinkReview.openInDrive')}
              </button>
            </div>
          )}
          {loadState === 'loading' && <div className="ws-drive-pill rounded-lg h-[104px] animate-pulse" aria-label={t('loading')} />}
          {link && (
            <LinkRow
              link={link}
              isFolder={brief?.target?.isFolder ?? false}
              // The edit button switches to the link's own org first when it lives elsewhere (`startEdit`): the edit
              // panel's requests act in the active org.
              canGrantEdit
              isBusy={switching}
              isEditing={editing && place.editableHere}
              onStartEdit={() => { void startEdit() }}
              onCancelEdit={() => setEditing(false)}
              onClose={() => setEditing(false)}
              onLinkPatched={(updated) => setLink(updated)}
              onRevoke={revoke}
              onCopy={copy}
              onShowQr={() => setShowQr((v) => !v)}
            />
          )}
          {showQr && link && (
            <div className="flex justify-center pt-2"><LinkQrCode url={link.url} /></div>
          )}
        </div>

        <header className="flex items-center gap-3">
          <AppTile name={via} host={via} />
          <div className="min-w-0">
            <p className="text-sm font-medium themed-text truncate">{t('shareLinkReview.createdThrough', { via })}</p>
            <p className="text-xs themed-text-muted truncate">
              {[who && t('shareLinkReview.actingAs', { who }), when].filter(Boolean).join(' · ')}
            </p>
          </div>
        </header>

        {revokedNote && (
          <p className="flex items-center gap-2 text-sm themed-text-muted">
            <Ban size={14} className="shrink-0 text-red-400" aria-hidden />{revokedNote}
          </p>
        )}
        {(loadState === 'error' || (loadState === 'ok' && !link)) && (
          <p className="text-sm text-red-400">{t('shareLinkReview.loadFailed')}</p>
        )}

        {live && <p className="text-xs leading-relaxed themed-text-muted">{t('shareLinkReview.decide')}</p>}
      </div>
    </Modal>
    {askSwitch && (
      <Modal
        glass
        title={t('shareLinkReview.switchTitle', { org: switchOrg })}
        onClose={() => askSwitch(false)}
        maxWidth="max-w-sm"
        footer={
          <>
            <button type="button" onClick={() => askSwitch(false)} className="rounded-lg px-3 py-1.5 text-sm themed-text themed-bg-hover">
              {t('cancel')}
            </button>
            <button type="button" onClick={() => askSwitch(true)} autoFocus className="rounded-lg px-4 py-1.5 text-sm font-medium themed-btn-primary">
              {t('shareLinkReview.switchConfirm')}
            </button>
          </>
        }
      >
        <p className="px-5 py-4 text-sm themed-text-muted">
          {target.orgEmail ? t('shareLinkReview.switchAs', { email: target.orgEmail }) : t('shareLinkReview.switchBody', { org: switchOrg })}
        </p>
      </Modal>
    )}
    </>
  )
}
